// Left-rail mode picker for /releases/new/chromium.
//
// Mirrors the source rail on the data-confident profiles publish
// page: a vertical stack of mode cards. The active card expands to
// reveal its per-mode input (catalog → Combobox, latest → resolved
// version readout, manual → free-text input). Inactive cards stay
// compact so the eye lands on the active mode.

import { Badge } from "@veil/ui/components/badge";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { EASE_OUT_EXPO } from "@/lib/motion";
import type { UpstreamRelease } from "@/lib/releases/types.ts";
import { CatalogVersionSelect } from "./catalog-version-select";
import { ManualVersionInput } from "./manual-version-input";

export type PublishChromiumMode = "latest" | "catalog" | "manual";

interface FormShape {
	mode: "latest" | "catalog" | "manual";
	version?: string;
}

export interface ModeOption {
	value: PublishChromiumMode;
	title: string;
	description: string;
	disabled?: boolean;
	disabledReason?: string;
}

interface Props {
	mode: PublishChromiumMode;
	onChangeMode: (next: PublishChromiumMode) => void;
	options: ModeOption[];
	showAdvanced: boolean;
	onRevealAdvanced: () => void;
	releases: UpstreamRelease[];
	publishedSet: Set<string>;
	catalogValue: string | undefined;
	onCatalogChange: (next: string) => void;
	catalogDisabled: boolean;
	latestUpstream: UpstreamRelease | null;
	latestAlreadyPublished: boolean;
	register: UseFormRegister<FormShape>;
	errors: FieldErrors<FormShape>;
	versionError?: string;
}

export function SourceRail({
	mode,
	onChangeMode,
	options,
	showAdvanced,
	onRevealAdvanced,
	releases,
	publishedSet,
	catalogValue,
	onCatalogChange,
	catalogDisabled,
	latestUpstream,
	latestAlreadyPublished,
	register,
	errors,
}: Props) {
	const reduce = useReducedMotion();

	return (
		<div className="flex flex-col gap-3">
			<h2 className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
				Source
			</h2>

			<div
				role="radiogroup"
				aria-label="Version source"
				className="flex flex-col gap-2"
			>
				{options.map((opt) => {
					const active = opt.value === mode;
					return (
						<div key={opt.value}>
							{/* biome-ignore lint/a11y/useSemanticElements: native radio cannot host rich child layout */}
							<button
								type="button"
								role="radio"
								aria-checked={active}
								disabled={opt.disabled}
								onClick={() => !opt.disabled && onChangeMode(opt.value)}
								className={cn(
									"group flex w-full flex-col gap-1 rounded-xl border px-3 py-2.5 text-left transition-all",
									active
										? "border-primary/40 bg-primary/5 ring-1 ring-primary/15"
										: "border-border bg-background/40 hover:border-foreground/20 hover:bg-background",
									opt.disabled && "cursor-not-allowed opacity-50",
								)}
							>
								<div className="flex items-baseline justify-between gap-2">
									<span
										className={cn(
											"text-sm font-medium",
											!active && "text-foreground/80",
										)}
									>
										{opt.title}
									</span>
									{opt.value === "latest" && latestAlreadyPublished ? (
										<Badge variant="secondary" className="text-[10px]">
											already published
										</Badge>
									) : null}
								</div>
								<span className="text-[11px] leading-snug text-muted-foreground">
									{opt.disabledReason ?? opt.description}
								</span>
							</button>

							<AnimatePresence initial={false}>
								{active ? (
									<motion.div
										key={`${opt.value}-panel`}
										initial={reduce ? false : { opacity: 0, y: -4 }}
										animate={{ opacity: 1, y: 0 }}
										exit={reduce ? undefined : { opacity: 0, y: -4 }}
										transition={{ duration: 0.24, ease: EASE_OUT_EXPO }}
										className="mt-2 px-1"
									>
										{opt.value === "catalog" ? (
											<div className="flex flex-col gap-2">
												<CatalogVersionSelect
													id="catalog-version"
													releases={releases}
													publishedSet={publishedSet}
													value={catalogValue}
													onChange={onCatalogChange}
													disabled={catalogDisabled}
												/>
												{releases.length > 0 ? (
													<RecentRows
														releases={releases}
														publishedSet={publishedSet}
														value={catalogValue}
														onChange={onCatalogChange}
													/>
												) : null}
											</div>
										) : null}
										{opt.value === "latest" ? (
											<LatestReadout
												latest={latestUpstream}
												alreadyPublished={latestAlreadyPublished}
											/>
										) : null}
										{opt.value === "manual" ? (
											<ManualVersionInput register={register} errors={errors} />
										) : null}
									</motion.div>
								) : null}
							</AnimatePresence>
						</div>
					);
				})}
			</div>

			{!showAdvanced ? (
				<button
					type="button"
					onClick={onRevealAdvanced}
					className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
				>
					Show advanced (manual version)
				</button>
			) : null}
		</div>
	);
}

function LatestReadout({
	latest,
	alreadyPublished,
}: {
	latest: UpstreamRelease | null;
	alreadyPublished: boolean;
}) {
	if (!latest) {
		return (
			<p className="text-xs text-muted-foreground">
				Resolved at run time from the upstream catalog.
			</p>
		);
	}
	return (
		<div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-border/60 bg-muted/20 p-3 text-xs">
			<div className="flex items-baseline justify-between gap-2">
				<span className="font-mono text-sm tabular-nums text-foreground">
					{latest.fullVersion}
				</span>
				<span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
					v{latest.browserMajor}
				</span>
			</div>
			<div className="flex items-center justify-between text-[11px] text-muted-foreground">
				<span>{formatRelative(latest.publishedAt)}</span>
				{latest.assetSizeBytes ? (
					<span className="tabular-nums">
						{formatBytes(latest.assetSizeBytes)}
					</span>
				) : null}
			</div>
			{alreadyPublished ? (
				<p className="text-[11px] text-amber-700 dark:text-amber-400">
					Already in the manifest. Sync the catalog or pick another mode.
				</p>
			) : null}
		</div>
	);
}

function RecentRows({
	releases,
	publishedSet,
	value,
	onChange,
}: {
	releases: UpstreamRelease[];
	publishedSet: Set<string>;
	value: string | undefined;
	onChange: (next: string) => void;
}) {
	const reduce = useReducedMotion();
	const recent = releases
		.filter((r) => r.hasX64Deb)
		.slice()
		.sort(
			(a, b) =>
				new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
		)
		.slice(0, 6);
	if (recent.length === 0) return null;
	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-baseline justify-between">
				<span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
					Recent
				</span>
				<span className="text-[10px] tabular-nums text-muted-foreground">
					{recent.length} of {releases.filter((r) => r.hasX64Deb).length}
				</span>
			</div>
			<ul className="flex flex-col gap-0.5">
				{recent.map((r, i) => {
					const active = r.fullVersion === value;
					const alreadyPublished = publishedSet.has(r.fullVersion);
					return (
						<motion.li
							key={r.tagName}
							initial={reduce ? false : { opacity: 0, y: 4 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								duration: 0.22,
								ease: EASE_OUT_EXPO,
								delay: i * 0.04,
							}}
						>
							<button
								type="button"
								disabled={alreadyPublished}
								onClick={() => onChange(r.fullVersion)}
								className={cn(
									"flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
									active
										? "bg-foreground/10 text-foreground"
										: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
									alreadyPublished &&
										"cursor-not-allowed opacity-50 hover:bg-transparent hover:text-muted-foreground",
								)}
							>
								<span className="font-mono tabular-nums">{r.fullVersion}</span>
								<span className="flex items-center gap-1.5">
									<span className="text-[10px]">
										{formatRelative(r.publishedAt)}
									</span>
									{alreadyPublished ? (
										<Badge variant="secondary" className="text-[10px]">
											published
										</Badge>
									) : null}
								</span>
							</button>
						</motion.li>
					);
				})}
			</ul>
		</div>
	);
}
